import React, { useState, useEffect, useCallback } from 'react';
import { CalendarDaysIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { format, startOfWeek, endOfWeek, parseISO, isValid } from 'date-fns';
import { useSessionManager } from '../../../hooks/autoplanner/useSessionManager';
import { autoplannerAPI } from '../../../services/api';
import StepHeader from './StepHeader';
import DateRangePicker from './DateRangePicker';
import SessionList from './SessionList';
import StepNavigation from './StepNavigation';

// Small helper component to keep top navigation state synced
const RegisterNavUpdater = ({ registerNav, onNext, onBack, canNext, canBack }) => {
  const prevRef = React.useRef(null);
  const onNextRef = React.useRef(onNext);
  const onBackRef = React.useRef(onBack);

  // Update refs when props change
  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    const payload = {
      onNext: onNextRef.current,
      onBack: onBackRef.current,
      canNext,
      canBack,
    };
    const prev = prevRef.current;
    const changed =
      !prev ||
      prev.onNext !== payload.onNext ||
      prev.onBack !== payload.onBack ||
      prev.canNext !== payload.canNext ||
      prev.canBack !== payload.canBack;
    if (changed) {
      registerNav(payload);
      prevRef.current = payload;
    }
  }, [registerNav, canNext, canBack]);
  return null;
};

const Step2SessionSelection = ({
  trainingGroupId,
  selectedSessions,
  startDate,
  endDate,
  taskId,
  onComplete,
  onBack,
  registerNav,
}) => {
  const [dateRange, setDateRange] = useState({
    start: startDate ? new Date(startDate) : startOfWeek(new Date()),
    end: endDate ? new Date(endDate) : endOfWeek(new Date()),
  });
  const [showScheduledSessions, setShowScheduledSessions] = useState(false);
  const [includeScheduledWarning, setIncludeScheduledWarning] = useState(false);
  const [currentSubstep, setCurrentSubstep] = useState('selection'); // 'selection' or 'ordering'
  const [sessionOrder, setSessionOrder] = useState({});
  const [createdTaskId, setCreatedTaskId] = useState(taskId);
  const [creating, setCreating] = useState(false);

  // Use refs to store latest values to avoid infinite re-renders
  const sessionsRef = React.useRef([]);
  const selectedSessionsListRef = React.useRef([]);
  const sessionOrderRef = React.useRef({});
  const dateRangeRef = React.useRef(dateRange);
  const onBackRef = React.useRef(onBack);

  const { sessions, selectedSessionsList, loading, handleSessionSelect, handleSelectAllSessions } =
    useSessionManager(trainingGroupId, selectedSessions, dateRange, showScheduledSessions);

  // Update refs when values change
  React.useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  React.useEffect(() => {
    selectedSessionsListRef.current = selectedSessionsList;
  }, [selectedSessionsList]);

  React.useEffect(() => {
    sessionOrderRef.current = sessionOrder;
  }, [sessionOrder]);

  React.useEffect(() => {
    dateRangeRef.current = dateRange;
  }, [dateRange]);

  React.useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  const handleDateRangeChange = (field, value) => {
    let nextDate = value;
    if (typeof value === 'string') {
      if (value.trim() === '') {
        return;
      }
      nextDate = parseISO(value);
    }

    if (!(nextDate instanceof Date) || !isValid(nextDate)) {
      return;
    }

    setDateRange((prev) => ({
      ...prev,
      [field]: nextDate,
    }));
  };

  // Helper function to format dates consistently as yyyy-mm-dd strings
  const formatDateString = (date) => {
    if (typeof date === 'string') {
      return date.split('T')[0];
    }
    return date.toISOString().split('T')[0];
  };

  // Helper function to format dates for display
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Handle session order changes
  const handleSessionOrderChange = (date, sessionIds) => {
    setSessionOrder((prev) => ({
      ...prev,
      [date]: sessionIds,
    }));
  };

  // Generate session order object from selected sessions
  const generateSessionOrder = (selectedSessionsData) => {
    const orderByDate = {};
    const sessionsByDate = {};

    selectedSessionsData.forEach((session) => {
      const dateStr = formatDateString(session.date);
      if (!sessionsByDate[dateStr]) {
        sessionsByDate[dateStr] = [];
      }
      sessionsByDate[dateStr].push(session);
    });

    Object.entries(sessionsByDate).forEach(([dateStr, sessions]) => {
      const sortedSessions = sessions.sort((a, b) => a.id - b.id);
      orderByDate[dateStr] = sortedSessions.map((session) => session.id);
    });

    return orderByDate;
  };

  const handleContinue = useCallback(async () => {
    if (currentSubstep === 'selection') {
      const selectedSessionsData = sessionsRef.current.filter((session) =>
        selectedSessionsListRef.current.includes(session.id)
      );
      const hasScheduledSessions = selectedSessionsData.some(
        (session) => session.session_type === 'scheduled'
      );
      if (hasScheduledSessions && !includeScheduledWarning) {
        setIncludeScheduledWarning(true);
        return;
      }

      // Check if we need to show session ordering
      const sessionsByDate = {};
      selectedSessionsData.forEach((session) => {
        const dateStr = formatDateString(session.date);
        if (!sessionsByDate[dateStr]) {
          sessionsByDate[dateStr] = [];
        }
        sessionsByDate[dateStr].push(session);
      });

      const datesWithMultipleSessions = Object.entries(sessionsByDate).filter(
        ([date, sessions]) => sessions.length > 1
      );

      if (datesWithMultipleSessions.length > 0) {
        // Initialize session order and move to ordering substep
        const defaultOrder = generateSessionOrder(selectedSessionsData);
        setSessionOrder(defaultOrder);
        setCurrentSubstep('ordering');
      } else {
        // No ordering UI needed, but still create task and save session order
        await completeStep2(selectedSessionsData, generateSessionOrder(selectedSessionsData));
      }
    } else if (currentSubstep === 'ordering') {
      // Complete the step with session order
      const selectedSessionsData = sessionsRef.current.filter((session) =>
        selectedSessionsListRef.current.includes(session.id)
      );
      await completeStep2(selectedSessionsData, sessionOrderRef.current);
    }
  }, [currentSubstep, includeScheduledWarning, creating]);

  const completeStep2 = async (selectedSessionsData, sessionOrderData) => {
    try {
      setCreating(true);

      let currentTaskId = createdTaskId;

      // Create autoplanner task if it doesn't exist
      if (!currentTaskId) {
        console.log('Creating new autoplanner task in Step 2');
        const newTask = await autoplannerAPI.createAutoplannerTask(
          trainingGroupId,
          selectedSessionsData,
          format(dateRangeRef.current.start, 'yyyy-MM-dd'),
          format(dateRangeRef.current.end, 'yyyy-MM-dd')
        );
        if (!newTask || !newTask.id) {
          throw new Error('Task creation failed: no task id returned');
        }
        currentTaskId = newTask.id;
        setCreatedTaskId(currentTaskId);
      }

      // Persist updated session selection to backend for this task
      try {
        const sessionIds = selectedSessionsData.map((s) => s.id);
        await autoplannerAPI.fixTaskSessions(currentTaskId, sessionIds);
      } catch (e) {
        console.warn('Fixing task sessions failed (continuing):', e);
      }

      // Save session order
      try {
        await autoplannerAPI.saveSessionOrder(currentTaskId, sessionOrderData);
      } catch (e) {
        console.warn('Saving session order failed (continuing):', e);
      }

      // Complete the step
      onComplete(
        selectedSessionsData,
        format(dateRangeRef.current.start, 'yyyy-MM-dd'),
        format(dateRangeRef.current.end, 'yyyy-MM-dd'),
        sessionOrderData,
        currentTaskId
      );
    } catch (error) {
      console.error('Error creating task or saving session order:', error);
      // Still proceed with completion but without task ID
      onComplete(
        selectedSessionsData,
        format(dateRangeRef.current.start, 'yyyy-MM-dd'),
        format(dateRangeRef.current.end, 'yyyy-MM-dd'),
        sessionOrderData,
        null
      );
    } finally {
      setCreating(false);
    }
  };

  const handleBack = useCallback(() => {
    if (currentSubstep === 'ordering') {
      setCurrentSubstep('selection');
    } else {
      onBackRef.current();
    }
  }, [currentSubstep]);

  const unscheduledSessions = sessions.filter((s) => s.session_type === 'unscheduled');
  const scheduledSessions = sessions.filter((s) => s.session_type === 'scheduled');

  // Render session ordering substep
  if (currentSubstep === 'ordering') {
    const selectedSessionsData = sessions.filter((session) =>
      selectedSessionsList.includes(session.id)
    );

    // Group sessions by date to find dates with multiple sessions
    const sessionsByDate = {};
    selectedSessionsData.forEach((session) => {
      const dateStr = formatDateString(session.date);
      if (!sessionsByDate[dateStr]) {
        sessionsByDate[dateStr] = [];
      }
      sessionsByDate[dateStr].push(session);
    });

    const datesWithMultipleSessions = Object.entries(sessionsByDate)
      .filter(([date, sessions]) => sessions.length > 1)
      .sort(([dateA], [dateB]) => {
        const timeA = new Date(dateA).getTime();
        const timeB = new Date(dateB).getTime();
        return timeA - timeB;
      });

    return (
      <div className="space-y-6">
        {registerNav && (
          // keep enabled state in sync while user edits
          <RegisterNavUpdater
            registerNav={registerNav}
            onNext={handleContinue}
            onBack={handleBack}
            canNext={!creating}
            canBack={true}
          />
        )}
        <StepHeader
          icon={CalendarDaysIcon}
          title="Session Order"
          description="Set the order for sessions that occur on the same day. This determines which session happens first."
        />

        <div className="rounded-lg bg-white shadow">
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-medium text-gray-900">Session Order Management</h3>
            <p className="mt-1 text-sm text-gray-600">
              Configure the sequence of sessions for dates with multiple training sessions.
            </p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {datesWithMultipleSessions.map(([dateStr, sessions]) => {
                const currentOrder = sessionOrder[dateStr] || sessions.map((s) => s.id);
                const orderedSessions = currentOrder
                  .map((id) => sessions.find((s) => s.id === id))
                  .filter(Boolean);

                return (
                  <div key={dateStr} className="rounded-lg border border-gray-200 p-4">
                    <h4 className="mb-3 text-sm font-medium text-gray-900">
                      {formatDate(dateStr)} ({sessions.length} sessions)
                    </h4>
                    <div className="space-y-2">
                      {orderedSessions.map((session, index) => (
                        <div
                          key={session.id}
                          className="flex items-center justify-between rounded bg-gray-50 p-3"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-800">
                              {index + 1}
                            </span>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{session.title}</p>
                              <p className="text-xs text-gray-500">{session.duration} minutes</p>
                            </div>
                          </div>
                          <div className="flex space-x-1">
                            {index > 0 && (
                              <button
                                onClick={() => {
                                  const newOrder = [...currentOrder];
                                  [newOrder[index], newOrder[index - 1]] = [
                                    newOrder[index - 1],
                                    newOrder[index],
                                  ];
                                  handleSessionOrderChange(dateStr, newOrder);
                                }}
                                className="p-1 text-gray-400 hover:text-gray-600"
                                title="Move up"
                              >
                                ↑
                              </button>
                            )}
                            {index < orderedSessions.length - 1 && (
                              <button
                                onClick={() => {
                                  const newOrder = [...currentOrder];
                                  [newOrder[index], newOrder[index + 1]] = [
                                    newOrder[index + 1],
                                    newOrder[index],
                                  ];
                                  handleSessionOrderChange(dateStr, newOrder);
                                }}
                                className="p-1 text-gray-400 hover:text-gray-600"
                                title="Move down"
                              >
                                ↓
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <StepNavigation
          onBack={handleBack}
          onContinue={handleContinue}
          continueDisabled={creating}
          backLabel="Back to Session Selection"
          continueLabel={creating ? 'Creating Task...' : 'Continue'}
        />
      </div>
    );
  }

  // Render session selection substep
  return (
    <div className="space-y-6">
      {typeof registerNav === 'function' && (
        <RegisterNavUpdater
          registerNav={registerNav}
          onNext={handleContinue}
          onBack={handleBack}
          canNext={selectedSessionsList.length > 0 && !creating}
          canBack={true}
        />
      )}
      <StepHeader
        icon={CalendarDaysIcon}
        title="Select Training Sessions"
        description="Choose the training sessions you want to include in the automated scheduling."
      />
      <DateRangePicker dateRange={dateRange} onDateRangeChange={handleDateRangeChange} />
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Session Types</h3>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showScheduledSessions}
                onChange={(e) => setShowScheduledSessions(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Include scheduled sessions</span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
          <div className="rounded-lg bg-gray-50 p-3">
            <div className="font-medium text-gray-900">Unscheduled Sessions</div>
            <div className="text-2xl font-bold text-blue-600">{unscheduledSessions.length}</div>
            <div className="text-gray-500">Available for planning</div>
          </div>
          <div className="rounded-lg bg-gray-50 p-3">
            <div className="font-medium text-gray-900">Scheduled Sessions</div>
            <div className="text-2xl font-bold text-green-600">{scheduledSessions.length}</div>
            <div className="text-gray-500">Already planned</div>
          </div>
          <div className="rounded-lg bg-gray-50 p-3">
            <div className="font-medium text-gray-900">Selected Sessions</div>
            <div className="text-2xl font-bold text-purple-600">{selectedSessionsList.length}</div>
            <div className="text-gray-500">For autoplanner</div>
          </div>
        </div>
      </div>
      {showScheduledSessions && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Scheduled Sessions Warning</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Including scheduled sessions will reschedule them through the autoplanner process.
                  This may affect existing crew assignments and timing.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="rounded-lg bg-white shadow">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-medium text-gray-900">
            Training Sessions ({sessions.length})
          </h3>
          <button
            type="button"
            onClick={handleSelectAllSessions}
            disabled={sessions.length === 0}
            className="inline-flex items-center rounded-md border border-purple-500 bg-purple-50 px-3 py-1 text-sm font-medium text-purple-700 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
          >
            Select All Sessions
          </button>
        </div>
        {loading ? (
          <div className="p-6 text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
            <p className="mt-2 text-sm text-gray-600">Loading sessions...</p>
          </div>
        ) : (
          <SessionList
            sessions={sessions}
            selectedSessionsList={selectedSessionsList}
            onSessionSelect={handleSessionSelect}
          />
        )}
      </div>
      {includeScheduledWarning && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-gray-600 bg-opacity-50">
          <div className="relative top-20 mx-auto w-96 rounded-md border bg-white p-5 shadow-lg">
            <div className="mt-3 text-center">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-yellow-400" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">Reschedule Warning</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  You have selected scheduled sessions that will be rescheduled through the
                  autoplanner process. This may affect existing crew assignments and timing.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={() => {
                    setIncludeScheduledWarning(false);
                    handleContinue();
                  }}
                  className="w-full rounded-md bg-yellow-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-300"
                >
                  Continue with Rescheduling
                </button>
                <button
                  onClick={() => setIncludeScheduledWarning(false)}
                  className="mt-2 w-full rounded-md bg-gray-300 px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <StepNavigation
        onBack={handleBack}
        onContinue={handleContinue}
        continueDisabled={selectedSessionsList.length === 0 || creating}
        backLabel="Back"
        continueLabel={(() => {
          if (creating) return 'Creating Task...';
          const selectedSessionsData = sessions.filter((session) =>
            selectedSessionsList.includes(session.id)
          );
          const sessionsByDate = {};
          selectedSessionsData.forEach((session) => {
            const dateStr = formatDateString(session.date);
            if (!sessionsByDate[dateStr]) {
              sessionsByDate[dateStr] = [];
            }
            sessionsByDate[dateStr].push(session);
          });
          const datesWithMultipleSessions = Object.entries(sessionsByDate).filter(
            ([date, sessions]) => sessions.length > 1
          );
          return datesWithMultipleSessions.length > 0 ? 'Continue to Order Sessions' : 'Continue';
        })()}
      />
    </div>
  );
};

export default Step2SessionSelection;
